import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import SPAButton from '../shared/SPAButton';
import SPAPageHeader from '../shared/SPAPageHeader';
import AddProgramMenu from './AddProgramMenu';
import AlertMessagesTable from './AlertMessages/AlertMessagesTable';
import ProgramsTable from './ProgramsTable';
import ReferenceMaterialsTable from './ReferenceMaterials/ReferenceMaterialsTable';

type ContentPage = 'alerts' | 'custom' | 'referenceMaterials' | 'standard';

interface PageDef {
  actions: React.ReactNode;
  content: React.ReactNode;
  path: string;
  title: string;
}

const PAGES: Record<ContentPage, PageDef> = {
  alerts: {
    actions: (
      <SPAButton asChild variant="primary">
        <a href={Routes.new_admin_alert_message_path()}>Add Alert Message</a>
      </SPAButton>
    ),
    content: <AlertMessagesTable />,
    path: 'alert-messages',
    title: 'Alert Messages',
  },
  custom: {
    actions: <AddProgramMenu />,
    content: <ProgramsTable key="custom" custom />,
    path: 'custom-programs',
    title: 'Custom Programs',
  },
  referenceMaterials: {
    actions: (
      <SPAButton asChild variant="primary">
        <a href={Routes.new_admin_reference_material_path()}>Add Reference Material</a>
      </SPAButton>
    ),
    content: <ReferenceMaterialsTable />,
    path: 'reference-materials',
    title: 'Reference Materials',
  },
  standard: {
    actions: <AddProgramMenu />,
    content: <ProgramsTable key="standard" custom={false} />,
    path: '',
    title: 'Programs',
  },
};

const pageFor = (pathname: string): ContentPage => (
  (Object.keys(PAGES) as ContentPage[])
    .find(key => PAGES[key].path && new RegExp(`/${PAGES[key].path}(/|$)`).test(pathname)) ?? 'standard'
);

// AIDEV-NOTE: The content pages — Programs, Custom Programs, Alert Messages, Reference Materials —
// share this one component and are reached from the rail, which lists them; there is no tab row.
// Each is a route (see programsPageRoutes) with its own header, whose action is the page's Add.
// The program tables' routed modals are nested under both program routes.
const ProgramsPage = () => {
  const { pathname } = useLocation();
  const page = PAGES[pageFor(pathname)];

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      <div className="tw-max-w-[1400px] tw-mx-auto tw-px-6 tw-py-4">
        <SPAPageHeader actions={page.actions} title={page.title} />
        {page.content}
      </div>
      <Outlet />
    </div>
  );
};

export default ProgramsPage;
